import type { KnitChapter } from "@/content";

// The knit-along's knitmap facts and video timing, from scripts/build-knit-along.py: per course
// the run of needles that knits ([-1, -1] when none) and the needles in use, and the second of
// the video each course starts at, plus where the last one ends.
export type KnitAlongData = {
  readonly needles: number;
  readonly courses: number;
  readonly runs: readonly (readonly [number, number])[];
  readonly spans: readonly (readonly [number, number])[];
  readonly courseStarts: readonly number[];
};

export type KnitAlongTimeline = KnitAlongData & {
  // Stitches knitted before each course, plus the total at the end.
  readonly stitchesBefore: readonly number[];
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const knittingIn = ({ runs }: KnitAlongData, course: number) =>
  runs[course][0] < 0 ? 0 : runs[course][1] - runs[course][0] + 1;

export const holdingIn = (data: KnitAlongData, course: number) =>
  data.spans[course][1] - data.spans[course][0] + 1 - knittingIn(data, course);

export const buildTimeline = (data: KnitAlongData): KnitAlongTimeline => {
  if (data.runs.length !== data.courses || data.spans.length !== data.courses) {
    throw new Error("The knit-along data does not cover every course");
  }
  if (data.courseStarts.length !== data.courses + 1) {
    throw new Error("The knit-along video timing does not cover every course");
  }
  const stitchesBefore = [0];
  for (let course = 0; course < data.courses; course += 1) {
    stitchesBefore.push(stitchesBefore[course] + knittingIn(data, course));
  }
  return { ...data, stitchesBefore };
};

// Progress counts courses: 312.4 means 312 courses are done and the 313th is 40% knitted.
export const progressAt = ({ courseStarts, courses }: KnitAlongTimeline, seconds: number): number => {
  if (seconds <= courseStarts[0]) return 0;
  if (seconds >= courseStarts[courses]) return courses;
  let low = 0;
  let high = courses;
  while (high - low > 1) {
    const middle = (low + high) >> 1;
    if (courseStarts[middle] <= seconds) low = middle;
    else high = middle;
  }
  return low + (seconds - courseStarts[low]) / (courseStarts[low + 1] - courseStarts[low]);
};

// The second of the video a course starts at; the last course's end for `courses`.
export const secondsAt = ({ courseStarts, courses }: KnitAlongTimeline, course: number): number =>
  courseStarts[clamp(Math.round(course), 0, courses)];

// The course on the needles at this progress, 0-based.
export const courseAt = ({ courses }: KnitAlongTimeline, progress: number): number =>
  clamp(Math.floor(progress), 0, courses - 1);

export const stitchesAt = (timeline: KnitAlongTimeline, progress: number): number => {
  const { stitchesBefore, courses } = timeline;
  if (progress >= courses) return stitchesBefore[courses];
  const course = courseAt(timeline, progress);
  return stitchesBefore[course] + Math.floor(clamp(progress - course, 0, 1) * knittingIn(timeline, course));
};

export const chapterAt = (chapters: readonly KnitChapter[], course: number): KnitChapter =>
  chapters.reduce((current, chapter) => (chapter.from <= course ? chapter : current), chapters[0]);

const timelines = new Map<string, Promise<KnitAlongTimeline>>();

export const loadTimeline = (src: string): Promise<KnitAlongTimeline> => {
  let pending = timelines.get(src);
  if (!pending) {
    pending = fetch(src)
      .then((response) => {
        if (!response.ok) throw new Error(`${src}: ${response.status}`);
        return response.json() as Promise<KnitAlongData>;
      })
      .then(buildTimeline);
    pending.catch(() => timelines.delete(src));
    timelines.set(src, pending);
  }
  return pending;
};
